import { useState } from "react";
import type { ManualScanRequest, ManualScanResponse } from "@pm-barcode/shared";
import { useApiClient } from "../hooks/use-api-client.ts";
import { RequestError } from "../api/client.ts";

export interface ManualProduct {
  id: number;
  name: string;
  /** "serial" | "lot" | "none" — when omitted the bag-number field is shown. */
  tracking?: string;
}

interface Props {
  workflow: "receipt" | "dispatch" | "transfer";
  pickingRef: string;
  /** Products the operator can pick from (the picking's expected products / scanned-so-far). */
  products: ManualProduct[];
  defaultProductId?: number;
  defaultBatch?: string;
  onAdded: (result: ManualScanResponse) => void;
  onClose: () => void;
}

/**
 * Manual bag entry for a damaged barcode. The operator picks the product and types the batch
 * (+ bag number for serial products); the bag is recorded via /scan/manual and added to the
 * session like a scan. On receipt a blank serial bag number auto-generates a marked placeholder;
 * on dispatch/transfer the bag number is required (you're picking a real, in-stock bag).
 */
export function ManualBagForm({ workflow, pickingRef, products, defaultProductId, defaultBatch, onAdded, onClose }: Props) {
  const api = useApiClient();
  const [productId, setProductId] = useState<string>(
    defaultProductId ? String(defaultProductId) : products.length === 1 ? String(products[0]!.id) : "",
  );
  const [batch, setBatch] = useState(defaultBatch ?? "");
  const [bagNumber, setBagNumber] = useState("");
  const [weight, setWeight] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = products.find((p) => String(p.id) === productId);
  const isSerial = selected?.tracking ? selected.tracking === "serial" : true;
  const needsBagNumber = isSerial && workflow !== "receipt";
  const canSubmit =
    !!productId && batch.trim().length > 0 && (!needsBagNumber || bagNumber.trim().length > 0) && !submitting;

  async function submit() {
    if (!api || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    const body: ManualScanRequest = {
      workflow,
      pickingRef,
      productId: Number(productId),
      batch: batch.trim(),
      bagNumber: isSerial && bagNumber.trim() ? bagNumber.trim() : undefined,
      weightKg: weight.trim() ? Number(weight) : undefined,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: new Date().toISOString(),
    };
    try {
      const result = await api.scanManual(body);
      onAdded(result);
    } catch (err) {
      setError(err instanceof RequestError || err instanceof Error ? err.message : "Failed to add bag");
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-base text-white focus:border-blue-500 focus:outline-none";

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 p-4">
      <div className="flex max-h-[85vh] w-full max-w-sm flex-col overflow-y-auto rounded-2xl bg-gray-800 p-5">
        <h3 className="text-lg font-bold">Add bag manually</h3>
        <p className="mb-3 mt-1 text-sm text-gray-400">For a damaged barcode. Same product, different bag.</p>

        {error && <p className="mb-3 rounded-lg bg-red-900/40 px-3 py-2 text-sm text-red-300">{error}</p>}

        <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">Product</label>
        <select value={productId} onChange={(e) => setProductId(e.target.value)} className={`${inputClass} mb-3`}>
          <option value="">Select product…</option>
          {products.map((p) => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>

        <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">Batch</label>
        <input value={batch} onChange={(e) => setBatch(e.target.value)} inputMode="numeric" placeholder="e.g. 1000046672" className={`${inputClass} mb-3 font-mono`} />

        {isSerial && (
          <>
            <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">
              Bag number {workflow === "receipt" ? "(blank = auto)" : ""}
            </label>
            <input value={bagNumber} onChange={(e) => setBagNumber(e.target.value)} inputMode="numeric" placeholder={workflow === "receipt" ? "leave blank if unreadable" : "from the label"} className={`${inputClass} mb-3 font-mono`} />
          </>
        )}

        <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">Weight kg (optional)</label>
        <input value={weight} onChange={(e) => setWeight(e.target.value)} inputMode="decimal" placeholder="e.g. 1000" className={`${inputClass} mb-4`} />

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600">
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="flex-1 rounded-xl bg-blue-600 py-3 font-semibold active:bg-blue-700 disabled:opacity-40"
          >
            {submitting ? "Adding…" : "Add bag"}
          </button>
        </div>
      </div>
    </div>
  );
}
